import { useNavigate } from "react-router-dom";
import { Reveal, SectionTag, ArrowLink, MaskedLines } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

/* one-line business purpose per service (homepage editorial index) */
const PURPOSES = {
  "business-websites": "For businesses that need a stronger digital presence and customer journey.",
  "custom-platforms": "For businesses requiring portals, workflows or digital platforms beyond a traditional website.",
  "crm-workflows": "For teams that need structured lead, customer and operational workflows.",
  automation: "For businesses spending too much time on repetitive manual processes.",
  "business-software": "For operating models that generic software cannot support effectively.",
  "technology-advisory": "For businesses that need clarity before making a technology decision.",
};

export default function ServicesPreview() {
  const navigate = useNavigate();
  return (
    <section
      id="services"
      data-testid="services-preview-section"
      className="scroll-mt-20 border-t border-white/5 bg-[#0C0B08] py-20 sm:py-24"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag index="04">Services</SectionTag>
        </Reveal>
        <h2
          data-testid="services-preview-headline"
          className="mt-6 max-w-2xl font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
        >
          <MaskedLines delay={0.1}>
            Technology for
            <br />
            <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
              businesses built to move forward.
            </span>
          </MaskedLines>
        </h2>

        <div className="mt-14 border-t border-white/10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={0.05 * i}>
              <button
                data-testid={`services-preview-item-${s.slug}`}
                onClick={() => navigate(`/services/${s.slug}`)}
                className="group relative grid w-full items-center gap-3 border-b border-white/10 py-8 text-left transition-colors duration-500 hover:border-[#D4AF37]/40 sm:grid-cols-[auto_1fr_auto_auto] sm:gap-10"
              >
                <span
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-gradient-to-r from-[#D4AF37]/60 to-transparent transition-transform duration-700 group-hover:scale-x-100"
                  aria-hidden="true"
                />
                <span className="font-serif text-2xl font-normal leading-none tracking-tight text-white/[0.08] transition-colors duration-700 group-hover:text-[#D4AF37]/25" aria-hidden="true">
                  {s.num}
                </span>
                <span>
                  <span className="block font-serif text-xl font-normal tracking-tight text-slate-100 transition-colors duration-500 group-hover:text-white sm:text-2xl">
                    {s.title}
                  </span>
                  <span className="mt-2 block max-w-lg text-sm font-normal leading-relaxed text-stone-500 transition-colors duration-500 group-hover:text-stone-300">
                    {PURPOSES[s.slug]}
                  </span>
                </span>
                <span className="sm:text-right">
                  <span className="block text-xs font-medium uppercase tracking-[0.2em] text-stone-400 transition-colors duration-500 group-hover:text-stone-300">
                    {s.label}
                  </span>
                  <span className="mt-1.5 block text-xs font-normal tracking-wide text-stone-500">
                    Starting {s.pricingIndia} · {s.pricingIntl}
                  </span>
                </span>
                <span
                  className="hidden text-[#E7CE8B] transition-transform duration-500 group-hover:translate-x-1.5 sm:block"
                  aria-hidden="true"
                >
                  →
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <ArrowLink to="/services" testid="services-preview-explore">
            Explore All Services
          </ArrowLink>
        </Reveal>
      </div>
    </section>
  );
}
